import { Helmet } from "react-helmet-async";
import { canonicalUrl, assetUrl } from "@/utils/siteConfig";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  /** Path beginning with "/" (e.g. "/tours/north-island"). Used for canonical and og:url. */
  path?: string;
  type?: string;
  noIndex?: boolean;
  /** One or many JSON-LD blocks. Each becomes its own <script>. */
  structuredData?: object | object[];
}

const SEO: React.FC<SEOProps> = ({
  title = "Ikhlas Tours - New Zealand Travel Experts",
  description = "Discover New Zealand with customisable itineraries, halal-friendly options, and round-the-clock support. Your perfect Kiwi adventure starts here.",
  image = "/assets/IMG_8397.JPG",
  path = "/",
  type = "website",
  noIndex = false,
  structuredData,
}) => {
  const siteTitle = title.includes("Ikhlas Tours")
    ? title
    : `${title} | Ikhlas Tours`;
  const fullUrl = canonicalUrl(path);
  const fullImage = assetUrl(image);
  const ldBlocks = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Ikhlas Tours" />
      <meta property="og:locale" content="en_MY" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Ikhlas Tours" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="NZ" />
      <meta name="geo.placename" content="New Zealand" />

      {/* Structured Data */}
      {ldBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
